import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { View, type ViewProps } from 'react-native';

import { auroraDay, moods, type Mood, type ThemeColors } from './moods';
import { fontFamily, palette, radii, shadows, space, type } from './tokens';

export type Theme = {
  mood: Mood;
  colors: ThemeColors;
  palette: typeof palette;
  space: typeof space;
  radii: typeof radii;
  type: typeof type;
  fontFamily: typeof fontFamily;
  shadows: typeof shadows;
};

const defaultTheme: Theme = {
  mood: 'aurora',
  colors: auroraDay,
  palette,
  space,
  radii,
  type,
  fontFamily,
  shadows,
};

const ThemeContext = createContext<Theme>(defaultTheme);

export function ThemeProvider({
  mood = 'aurora',
  children,
}: {
  mood?: Mood;
  children: ReactNode;
}) {
  const theme = useMemo<Theme>(
    () => ({
      mood,
      colors: moods[mood],
      palette,
      space,
      radii,
      type,
      fontFamily,
      shadows,
    }),
    [mood],
  );
  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Theme {
  return useContext(ThemeContext);
}

export type MoodSectionProps = ViewProps & {
  mood: Mood;
  children: ReactNode;
};

export function MoodSection({ mood, style, children, ...rest }: MoodSectionProps) {
  const theme = useMemo<Theme>(
    () => ({
      mood,
      colors: moods[mood],
      palette,
      space,
      radii,
      type,
      fontFamily,
      shadows,
    }),
    [mood],
  );
  return (
    <ThemeContext.Provider value={theme}>
      <View style={[{ backgroundColor: theme.colors.bg }, style]} {...rest}>
        {children}
      </View>
    </ThemeContext.Provider>
  );
}
